import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Loader2, AlertTriangle, X } from '@/components/icons';
import { useAuth } from '../Auth';
import { checkout } from '../../lib/auth-client';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PricingPlan {
  name: string;
  slug: string | null;
  productId?: string; // Direct product ID for checkout
  price: string;
  period: string;
  tagline: string;
  features: PlanFeature[];
  featured?: boolean;
  buttonText: string;
}

const plans: PricingPlan[] = [
  {
    name: 'Free',
    slug: null,
    price: '$0',
    period: 'forever',
    tagline: 'For starting out',
    buttonText: 'Start for free',
    features: [
      { text: 'Up to 10 diagrams', included: true },
      { text: 'All tech icons', included: true },
      { text: 'Export as PNG', included: true },
      { text: 'AI diagram generation', included: true },
      { text: 'Export as SVG', included: false },
      { text: 'Unlimited diagrams', included: false },
      { text: 'Priority support', included: false },
    ],
  },
  {
    name: 'Pro',
    slug: 'pro',
    price: '$4.99',
    period: 'per month',
    tagline: 'For professionals',
    featured: true,
    buttonText: 'Start with Pro',
    features: [
      { text: 'Unlimited diagrams', included: true },
      { text: 'All tech icons', included: true },
      { text: 'Export as PNG & SVG', included: true },
      { text: 'AI diagram generation', included: true },
      { text: 'Priority support', included: true },
      { text: 'Early access to features', included: true },
      { text: 'Cancel anytime', included: true },
    ],
  },
  {
    name: 'Lifetime',
    slug: 'lifetime',
    price: '$49.99',
    period: 'one-time',
    tagline: 'For fans',
    buttonText: 'Get lifetime access',
    features: [
      { text: 'Everything in Pro', included: true },
      { text: 'Lifetime access', included: true },
      { text: 'All future updates', included: true },
      { text: 'No recurring payments', included: true },
      { text: 'Premium support', included: true },
      { text: 'Early access to beta', included: true },
      { text: 'One-time payment', included: true },
      { text: 'Best value', included: true },
    ],
  },
];

export function PricingPlans() {
  const { isAuthenticated, user } = useAuth();
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Guards against firing checkout twice for the same plan intent
  // (StrictMode double-effects, re-renders while the session loads).
  const autoCheckoutSlug = useRef<string | null>(null);

  // Get current user's subscription tier (default to 'free')
  const currentTier = (user as { subscriptionTier?: string } | null)?.subscriptionTier || 'free';

  const isPlanActive = (planName: string) => {
    return currentTier.toLowerCase() === planName.toLowerCase();
  };

  const canUpgrade = (planName: string) => {
    const tierOrder = ['free', 'pro', 'lifetime'];
    const currentIndex = tierOrder.indexOf(currentTier.toLowerCase());
    const planIndex = tierOrder.indexOf(planName.toLowerCase());
    return planIndex > currentIndex;
  };

  // Plan intent carried from a landing-page CTA (/pricing?plan=pro|lifetime):
  // go straight into Polar checkout for the chosen plan, then strip the param
  // so refreshes and back-navigation don't re-trigger it.
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan !== 'pro' && plan !== 'lifetime') return;
    if (autoCheckoutSlug.current === plan) return;
    if (!isAuthenticated || !user) return;

    const next = new URLSearchParams(searchParams);
    next.delete('plan');
    setSearchParams(next, { replace: true });

    const planName = plan === 'pro' ? 'Pro' : 'Lifetime';
    if (!isPlanActive(planName) && canUpgrade(planName)) {
      autoCheckoutSlug.current = plan;
      handleCheckout(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthenticated, user]);

  const handleCheckout = async (slug: string, productId?: string) => {
    if (!isAuthenticated) {
      setError('Please sign in to subscribe');
      return;
    }

    // If Pro user is upgrading to Lifetime, show confirmation first
    if (currentTier === 'pro' && slug === 'lifetime' && !showUpgradeConfirm) {
      setShowUpgradeConfirm(true);
      return;
    }

    setShowUpgradeConfirm(false);
    setLoadingSlug(slug);
    setError(null);

    try {
      // Try using slug first (if products are configured on backend)
      // Fall back to products array with product ID
      const checkoutParams = productId
        ? { products: [productId] }
        : { slug };

      const result = await checkout(checkoutParams);

      // If there's an error in the result, show it
      if (result?.error) {
        console.error('Checkout error:', result.error);
        setError(result.error.message || 'Failed to start checkout. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Checkout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start checkout. Please try again.';
      setError(errorMessage);
    } finally {
      setLoadingSlug(null);
    }
  };

  return (
    <div className="px-6 pb-16 pt-8 md:px-8">
      <div className="mx-auto max-w-6xl">

        {/* Title Section — matches other app screens */}
        <div className="mb-10 max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            Free until you outgrow it
          </h1>
          <p className="text-sm text-muted-foreground">
            Then five bucks a month, or one coffee run for lifetime access. No
            card up front, cancel whenever.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-center">
            {error}
          </div>
        )}

        {/* Pricing Cards — same design as the landing page */}
        <div className="grid gap-4 md:grid-cols-3 md:gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const featured = plan.featured;
            const active = isPlanActive(plan.name);
            const upgradeable = !active && plan.slug !== null && canUpgrade(plan.name);

            return (
              <div
                key={plan.name}
                className={`flex flex-col rounded-2xl p-7 md:p-8 ${
                  featured
                    ? 'bg-[linear-gradient(180deg,#010104_0%,#0D41FF_160%)] text-white'
                    : 'border border-border bg-card'
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="text-lg font-semibold tracking-tight">{plan.name}</h3>
                  <span
                    className={`text-xs font-medium ${
                      featured ? 'text-white/50' : 'text-muted-foreground'
                    }`}
                  >
                    {plan.tagline}
                  </span>
                </div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-semibold tracking-tight">
                    {plan.price}
                  </span>
                  <span className={`text-sm ${featured ? 'text-white/50' : 'text-muted-foreground'}`}>
                    {plan.period}
                  </span>
                </div>

                <ul className="mt-8 flex flex-1 flex-col gap-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature.text}
                      className={`flex items-center gap-2.5 text-sm md:text-[15px] ${
                        !feature.included
                          ? featured
                            ? 'text-white/40'
                            : 'text-muted-foreground/60'
                          : featured
                            ? 'text-white/75'
                            : 'text-foreground/70'
                      }`}
                    >
                      {feature.included ? (
                        <Check
                          className={`h-4 w-4 shrink-0 ${
                            featured ? 'text-white' : 'text-primary'
                          }`}
                        />
                      ) : (
                        <X
                          className={`h-4 w-4 shrink-0 ${
                            featured ? 'text-white/40' : 'text-muted-foreground/50'
                          }`}
                        />
                      )}
                      {feature.text}
                    </li>
                  ))}
                </ul>

                {active ? (
                  <button
                    disabled
                    className={`mt-9 w-full rounded-xl px-5 py-3 text-center text-sm font-medium leading-none cursor-default ${
                      featured
                        ? 'bg-white/15 text-white'
                        : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    }`}
                  >
                    ✓ Current plan
                  </button>
                ) : upgradeable ? (
                  <button
                    onClick={() => handleCheckout(plan.slug!, plan.productId)}
                    disabled={loadingSlug === plan.slug}
                    className={`mt-9 w-full rounded-xl px-5 py-3 text-center text-sm font-medium leading-none transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed ${
                      featured
                        ? 'bg-white text-slate-900 hover:bg-[#FF1240] hover:text-white'
                        : 'border border-border text-foreground hover:border-primary/40 hover:text-primary'
                    }`}
                  >
                    {loadingSlug === plan.slug ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      plan.buttonText
                    )}
                  </button>
                ) : (
                  <button
                    disabled
                    className={`mt-9 w-full rounded-xl px-5 py-3 text-center text-sm font-medium leading-none cursor-default ${
                      featured
                        ? 'bg-white/10 text-white/50'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {plan.slug === null ? 'Free tier' : 'Not available'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ / Trust Section */}
        <div className="mt-20 text-center">
          <p className="text-sm text-muted-foreground">
            🔒 Secure payments powered by Polar. Cancel anytime.
          </p>
        </div>
      </div>

      {/* Upgrade Confirmation Modal */}
      {showUpgradeConfirm && (
        <div
          onClick={() => setShowUpgradeConfirm(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            className="relative max-w-md w-full rounded-xl p-6 bg-card border border-border shadow-2xl"
          >
            <button
              onClick={() => setShowUpgradeConfirm(false)}
              aria-label="Close"
              className="absolute top-4 right-4 p-1 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-foreground">
                Upgrade to Lifetime?
              </h3>
            </div>

            <p className="mb-4 text-foreground">
              You currently have an active <strong>Pro subscription</strong> at $4.99/month.
            </p>

            <p className="mb-6 text-foreground">
              If you upgrade to <strong>Lifetime</strong> ($49.99 one-time):
            </p>

            <ul className="mb-6 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                Your Pro subscription will be <strong>automatically cancelled</strong>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                You won&rsquo;t be charged $4.99/month anymore
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                You&rsquo;ll have lifetime access with no recurring fees
              </li>
            </ul>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeConfirm(false)}
                className="flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm bg-muted text-foreground hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const lifetimePlan = plans.find(p => p.slug === 'lifetime');
                  handleCheckout('lifetime', lifetimePlan?.productId);
                }}
                disabled={loadingSlug === 'lifetime'}
                className="flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {loadingSlug === 'lifetime' ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  'Yes, Upgrade to Lifetime'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingPlans;
